/**
 * Topbar — per docs/specs/README.md
 * Breadcrumbs, command-palette trigger (search input), environment pill, user chip.
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-19-v2
 */
import { useLocation } from 'react-router-dom';
import { ChevronRight, LogOut, Search } from 'lucide-react';
import { findRoute } from '../../lib/routes';
import { getEnvironment } from '../../lib/env';
import { useAdminAuthStore, useAdminSession } from '../../lib/auth';

export function Topbar({ onOpenPalette }: { onOpenPalette?: () => void } = {}) {
    const loc = useLocation();
    const route = findRoute(loc.pathname);
    const env = getEnvironment();
    const session = useAdminSession();
    const signOut = useAdminAuthStore((state) => state.signOut);

    const handleOpen = () => {
        onOpenPalette?.();
    };

    return (
        <header className="admin-topbar">
            <nav className="topbar-crumbs" aria-label="Breadcrumb">
                <span>Admin</span>
                <ChevronRight size={12} />
                <span className="current">{route?.label ?? 'Unknown'}</span>
            </nav>

            <div className="topbar-spacer" />

            <button
                type="button"
                className="topbar-search topbar-search-button"
                onClick={handleOpen}
                aria-label="Open command palette"
            >
                <Search size={13} />
                <span className="topbar-search-placeholder">Search users, tickets, orgs…</span>
                <kbd>{'\u2318'}K</kbd>
            </button>

            <div className="topbar-env" data-env={env}>
                <span className="topbar-env-dot" />
                {env}
            </div>

            <div className="topbar-user-group">
                <button className="topbar-user" type="button" title={session.email}>
                    <span className="avatar">{session.initials}</span>
                    <span className="meta">
                        <span className="name">{session.name}</span>
                        <span className="role">{session.role.replace('_', ' ')}</span>
                    </span>
                </button>

                <button
                    className="btn btn-ghost btn-sm"
                    type="button"
                    onClick={() => void signOut()}
                >
                    <LogOut size={12} /> Sign out
                </button>
            </div>
        </header>
    );
}
